import { useEffect, useRef, useState } from "react";
import { Coffee, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const CafeChat = ({ onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/chat/messages");
      setMessages(r.data.messages);
      await api.post("/chat/read");
    } catch { /* silencieux : le salon reste tel quel */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/chat/messages", { text });
      setMessages((prev) => [...prev, r.data.message]);
      setDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible — réessayez.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/chat/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message effacé.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="flex flex-col h-full border border-white/10 rounded-2xl bg-white/[0.02] overflow-hidden" data-testid="cafe-chat">
      <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 shrink-0">
        <div>
          <h2 className="font-serif text-2xl">Le Café <span className="italic text-gold">du 30</span></h2>
          <p className="text-[11px] font-mono text-[#A0B2CE] tracking-[0.12em] uppercase mt-0.5">
            Salon commun — texte uniquement, pas de pièces jointes
          </p>
        </div>
        <button onClick={onClose} data-testid="cafe-close-btn" title="Fermer le salon"
          className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-[#A0B2CE] hover:text-white hover:border-gold/50 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 space-y-4" data-testid="cafe-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-[#A0B2CE]" data-testid="cafe-empty">
            <Coffee className="w-10 h-10 text-gold/60" />
            <p className="font-serif text-xl text-[#F3F4F6]">Bienvenue au Café du 30</p>
            <p className="text-sm max-w-md">Le salon de conversation entre le 30 du mois et toutes les compagnies. Lancez la discussion !</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.author_email === user?.email;
          const isAdmin = m.author_role === "admin";
          return (
            <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`cafe-message-${m.id}`}>
              <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 ${
                mine ? "bg-gold text-ink rounded-br-sm" : "bg-white/[0.06] border border-white/10 rounded-bl-sm"}`}>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${mine ? "text-ink/70" : isAdmin ? "text-gold" : "text-[#A0B2CE]"}`}>
                    {m.author_name}{isAdmin ? " · Le 30" : ""}
                  </span>
                  <span className={`text-[10px] ${mine ? "text-ink/50" : "text-[#A0B2CE]/70"}`}>{fmtDate(m.created_at)}</span>
                  {(mine || user?.role === "admin") && (
                    <button onClick={() => remove(m.id)} data-testid={`cafe-delete-${m.id}`} title="Effacer ce message"
                      className={`opacity-0 group-hover:opacity-100 transition-opacity ${mine ? "text-ink/60 hover:text-ink" : "text-[#A0B2CE] hover:text-red-400"}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-3 border-t border-white/10 px-4 md:px-6 py-4 shrink-0">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={2000}
          data-testid="cafe-input"
          placeholder="Écrire un message… (texte uniquement — pour un fichier, contactez Le 30 du mois)"
          className="flex-1 bg-white border border-white/40 focus:border-gold outline-none rounded-full px-5 py-3 text-sm text-ink placeholder:text-[#5A6B87] transition-colors"
        />
        <button type="submit" disabled={busy || !draft.trim()} data-testid="cafe-send-btn" title="Envoyer"
          className="w-11 h-11 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-[#B5952F] transition-colors disabled:opacity-40 shrink-0">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};
